import { useState } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Ban, Crown, Flag, User, X } from 'lucide-react'
import { type PartnerProfile, type ReportReason, blockUser, reportUser, unmatch } from '../lib/api'
import { hapticImpact } from '../lib/haptics'
import { openExternalLink } from '../lib/telegram'

const REPORT_REASON_KEYS: { value: ReportReason; labelKey: string }[] = [
  { value: 'spam', labelKey: 'reportReasons.spam' },
  { value: 'fake', labelKey: 'reportReasons.fake' },
  { value: 'inappropriate', labelKey: 'reportReasons.inappropriate' },
  { value: 'harassment', labelKey: 'reportReasons.harassment' },
]

/** Which action overlay is open, on top of the profile sheet itself. */
type ActionSheetMode = 'none' | 'actions' | 'report' | 'block_confirm' | 'unmatch_confirm'

interface ProfileDetailSheetProps {
  profile: PartnerProfile
  onClose: () => void
  /**
   * Present only when opened from a match — renders the Message/Report/
   * Block/Unmatch action footer, relocated here from the retired ChatPage
   * (docs/bot-migration-decision.md's "Phase 1.5 — Chat retirement"; a
   * match now hands off to a direct Telegram DM instead of an in-app chat
   * room, see MatchesPage). Absent when opened from the swipe deck
   * (SwipeCard), which has no match yet and no action footer.
   */
  matchActions?: {
    matchId: number
    partnerUserId: number
    /** Called after a successful block or unmatch so the caller can close
     * this sheet and refresh its list. */
    onGone: () => void
  }
}

export default function ProfileDetailSheet({ profile, onClose, matchActions }: ProfileDetailSheetProps) {
  const { t } = useTranslation()
  const [photoIndex, setPhotoIndex] = useState(0)
  const [actionSheet, setActionSheet] = useState<ActionSheetMode>('none')
  const [reportDone, setReportDone] = useState(false)

  async function handleReport(reason: ReportReason) {
    if (!matchActions) return
    setActionSheet('none')
    await reportUser(matchActions.partnerUserId, reason).catch(() => {})
    hapticImpact('medium')
    setReportDone(true)
    setTimeout(() => setReportDone(false), 2000)
  }

  async function handleBlock() {
    if (!matchActions) return
    setActionSheet('none')
    try {
      await blockUser(matchActions.partnerUserId)
      hapticImpact('heavy')
      matchActions.onGone()
    } catch {
      // silently drop — the sheet just stays open
    }
  }

  async function handleUnmatch() {
    if (!matchActions) return
    setActionSheet('none')
    try {
      await unmatch(matchActions.matchId)
      hapticImpact('medium')
      matchActions.onGone()
    } catch {
      // silently drop
    }
  }

  return (
    <div
      className="absolute inset-0 z-20 flex flex-col justify-end bg-black/50"
      onClick={onClose}
    >
      <motion.div
        className="max-h-[90dvh] overflow-y-auto rounded-t-2xl bg-tg-bg"
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        onClick={e => e.stopPropagation()}
      >
        {/* Photo gallery */}
        <div className="relative h-72 shrink-0 overflow-hidden bg-tg-secondary-bg">
          {profile.photo_urls.length > 0 ? (
            <img
              src={profile.photo_urls[photoIndex]}
              alt={profile.display_name}
              className="h-full w-full object-cover"
              draggable={false}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-tg-hint">
              <User className="h-16 w-16" />
            </div>
          )}

          {/* Dot indicator */}
          {profile.photo_urls.length > 1 && (
            <div className="absolute left-0 right-0 top-3 flex justify-center gap-1">
              {profile.photo_urls.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i === photoIndex ? 'w-4 bg-white' : 'w-1.5 bg-white/50'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Photo tap zones */}
          {profile.photo_urls.length > 1 && (
            <>
              <button
                className="absolute bottom-0 left-0 top-0 w-2/5 focus:outline-none"
                onClick={() => setPhotoIndex(i => Math.max(0, i - 1))}
                aria-label={t('swipeCard.previousPhoto')}
              />
              <button
                className="absolute bottom-0 right-0 top-0 w-3/5 focus:outline-none"
                onClick={() => setPhotoIndex(i => Math.min(profile.photo_urls.length - 1, i + 1))}
                aria-label={t('swipeCard.nextPhoto')}
              />
            </>
          )}
        </div>

        {/* Profile info */}
        <div className="px-5 pb-8 pt-4">
          {/* Name + age */}
          <p className="flex items-center gap-1.5 text-xl font-bold text-tg-text">
            {profile.display_name}, {profile.age}
            {profile.crown && (
              <Crown
                className="h-4 w-4 shrink-0 fill-current text-amber-400"
                aria-label={t('swipeCard.crownBadge')}
              />
            )}
          </p>

          {/* City + optional distance */}
          {profile.city && (
            <p className="mt-0.5 text-sm text-tg-hint">
              {profile.city}
              {profile.distance_km != null && (
                <>
                  {' · '}
                  {t('common.kmAway', { km: profile.distance_km < 1 ? '< 1' : profile.distance_km.toFixed(1) })}
                </>
              )}
            </p>
          )}

          {/* Bio */}
          {profile.bio && (
            <div className="mt-4">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-tg-hint">{t('profileDetail.about')}</p>
              <p className="text-sm leading-relaxed text-tg-text">{profile.bio}</p>
            </div>
          )}

          {/* Interests */}
          {profile.interests && profile.interests.length > 0 && (
            <div className="mt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-tg-hint">{t('profileDetail.interests')}</p>
              <div className="flex flex-wrap gap-2">
                {profile.interests.map(tag => (
                  <span
                    key={tag}
                    className="rounded-full bg-tg-secondary-bg px-3 py-1 text-xs text-tg-text"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Education + Height — shown only when present */}
          {(profile.education || profile.height_cm) && (
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1">
              {profile.education && (
                <p className="text-sm text-tg-text">
                  <span className="text-tg-hint">{t('profileDetail.education')} </span>
                  {profile.education}
                </p>
              )}
              {profile.height_cm ? (
                <p className="text-sm text-tg-text">
                  <span className="text-tg-hint">{t('profileDetail.height')} </span>
                  {t('profileDetail.heightCm', { height: profile.height_cm })}
                </p>
              ) : null}
            </div>
          )}

          {/* Match handoff — direct Telegram DM (see MatchesPage's own
              "Message on Telegram" row for the no-username fallback state;
              this sheet's button only renders when reachable, since here
              it's a secondary action alongside Report/Block/Unmatch). */}
          {matchActions && profile.telegram_username && (
            <button
              onClick={() => openExternalLink(`https://t.me/${profile.telegram_username}`)}
              className="mt-6 w-full rounded-xl bg-primary py-3 text-sm font-semibold text-white active:opacity-70"
            >
              {t('matches.messageOnTelegram')}
            </button>
          )}

          {matchActions && (
            <button
              onClick={() => setActionSheet('actions')}
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-tg-secondary-bg py-3 text-sm font-medium text-red-500 active:opacity-70"
            >
              <Flag className="h-4 w-4 fill-current" />
              {t('matchActions.actions')}
            </button>
          )}

          {/* Close button */}
          <button
            onClick={onClose}
            className={`w-full rounded-xl bg-tg-secondary-bg py-3 text-sm font-medium text-tg-text active:opacity-70 ${
              matchActions ? 'mt-2' : 'mt-6'
            }`}
          >
            {t('profileDetail.close')}
          </button>
        </div>
      </motion.div>

      {/* Main action sheet: Report / Block / Unmatch */}
      {actionSheet === 'actions' && (
        <div
          className="absolute inset-0 z-30 flex flex-col justify-end bg-black/50"
          onClick={() => setActionSheet('none')}
        >
          <div
            className="rounded-t-2xl bg-tg-bg px-4 pb-8 pt-4"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="mb-3 text-center text-sm font-semibold text-tg-text">
              {t('matchActions.actions')}
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => setActionSheet('report')}
                className="flex items-center gap-2 rounded-xl bg-tg-secondary-bg px-4 py-3 text-left text-sm text-tg-text active:opacity-70"
              >
                <Flag className="h-4 w-4 fill-current text-red-500" />
                {t('matchActions.reportUser')}
              </button>
              <button
                onClick={() => setActionSheet('block_confirm')}
                className="flex items-center gap-2 rounded-xl bg-tg-secondary-bg px-4 py-3 text-left text-sm text-red-500 active:opacity-70"
              >
                <Ban className="h-4 w-4" />
                {t('matchActions.blockUser')}
              </button>
              <button
                onClick={() => setActionSheet('unmatch_confirm')}
                className="flex items-center gap-2 rounded-xl bg-tg-secondary-bg px-4 py-3 text-left text-sm text-tg-hint active:opacity-70"
              >
                <X className="h-4 w-4" />
                {t('matchActions.unmatch')}
              </button>
              <button
                onClick={() => setActionSheet('none')}
                className="mt-1 rounded-xl bg-tg-secondary-bg px-4 py-3 text-center text-sm font-medium text-red-500 active:opacity-70"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report sheet */}
      {actionSheet === 'report' && (
        <div
          className="absolute inset-0 z-30 flex flex-col justify-end bg-black/50"
          onClick={() => setActionSheet('none')}
        >
          <div
            className="rounded-t-2xl bg-tg-bg px-4 pb-8 pt-4"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="mb-3 text-center text-sm font-semibold text-tg-text">
              {t('matchActions.reportUserPlain')}
            </p>
            <div className="flex flex-col gap-2">
              {REPORT_REASON_KEYS.map((r) => (
                <button
                  key={r.value}
                  onClick={() => handleReport(r.value)}
                  className="rounded-xl bg-tg-secondary-bg px-4 py-3 text-left text-sm text-tg-text active:opacity-70"
                >
                  {t(r.labelKey)}
                </button>
              ))}
              <button
                onClick={() => setActionSheet('none')}
                className="mt-1 rounded-xl bg-tg-secondary-bg px-4 py-3 text-center text-sm font-medium text-red-500 active:opacity-70"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Block confirmation */}
      {actionSheet === 'block_confirm' && (
        <div
          className="absolute inset-0 z-30 flex flex-col justify-end bg-black/50"
          onClick={() => setActionSheet('none')}
        >
          <div
            className="rounded-t-2xl bg-tg-bg px-4 pb-8 pt-4"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="mb-1 text-center text-base font-semibold text-tg-text">
              {t('matchActions.blockConfirm', { name: profile.display_name })}
            </p>
            <p className="mb-4 text-center text-sm text-tg-hint">{t('matchActions.blockWarning')}</p>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleBlock}
                className="rounded-xl bg-red-500 px-4 py-3 text-center font-semibold text-white active:opacity-70"
              >
                {t('matchActions.block')}
              </button>
              <button
                onClick={() => setActionSheet('none')}
                className="rounded-xl bg-tg-secondary-bg px-4 py-3 text-center text-sm text-tg-hint active:opacity-70"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unmatch confirmation */}
      {actionSheet === 'unmatch_confirm' && (
        <div
          className="absolute inset-0 z-30 flex flex-col justify-end bg-black/50"
          onClick={() => setActionSheet('none')}
        >
          <div
            className="rounded-t-2xl bg-tg-bg px-4 pb-8 pt-4"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="mb-1 text-center text-base font-semibold text-tg-text">
              {t('matchActions.unmatchConfirm', { name: profile.display_name })}
            </p>
            <p className="mb-4 text-center text-sm text-tg-hint">{t('matchActions.unmatchWarning')}</p>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleUnmatch}
                className="rounded-xl bg-tg-secondary-bg px-4 py-3 text-center font-semibold text-red-500 active:opacity-70"
              >
                {t('matchActions.unmatchAction')}
              </button>
              <button
                onClick={() => setActionSheet('none')}
                className="rounded-xl bg-tg-secondary-bg px-4 py-3 text-center text-sm text-tg-hint active:opacity-70"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report success toast */}
      {reportDone && (
        <div className="absolute inset-x-4 top-16 z-30 flex items-center justify-center rounded-xl bg-black/70 py-2 text-sm text-white">
          {t('matchActions.reportSubmitted')}
        </div>
      )}
    </div>
  )
}
